import { createSupabaseClient } from "../../../hooks/useSupabase";
import { PatientCustomerRelationship, PatientInMerge } from "./types";

// Search results (AC15) don't carry everything the merge column needs (MRN,
// address, race, sex) — this fetches the full row + linked customer +
// appointment count whenever a patient is added to the merge (mirrors
// getSelfPatient.ts's direct-Supabase-read pattern).
const getPatientForMerge = async (
  patientId: string,
): Promise<PatientInMerge | null> => {
  const supabase = createSupabaseClient();

  const { data: patient, error } = await supabase
    .from("patients")
    .select(
      `
      id, mrn, first_name, last_name, date_birth, city, state, postal_code, country, race, ethnicity, sex, email,
      guardian:legal_guardian_id ( id, first_name, last_name, email, mobile_phone_number ),
      family:family_contact_id ( id, first_name, last_name, email, mobile_phone_number )
      `,
    )
    .eq("id", patientId)
    .maybeSingle();

  if (error || !patient) return null;

  // Full appointment rows (not just a count) — also feeds the proactive
  // AC9/AC10 checkout/conflict checks (see checkPatientMergeBlockers.ts),
  // not just the AC5 header count.
  const { data: appointmentRows } = await supabase
    .from("appointment_patient_details")
    .select(
      "appointment:appointment_id!inner(id, status, time_slot:time_slot_id!inner(started_at, ended_at))",
    )
    .eq("patient_id", patientId);

  const appointments = (appointmentRows ?? [])
    .map((row) => {
      const appt = Array.isArray(row.appointment)
        ? row.appointment[0]
        : row.appointment;
      if (!appt) return null;
      const timeSlot = Array.isArray(appt.time_slot)
        ? appt.time_slot[0]
        : appt.time_slot;
      if (!timeSlot) return null;
      return {
        id: appt.id,
        status: appt.status,
        startedAt: timeSlot.started_at,
        endedAt: timeSlot.ended_at,
      };
    })
    .filter((a): a is NonNullable<typeof a> => a !== null);

  // AC5/AC41 — appointment count excludes Checkout, Incomplete, Abandoned,
  // Canceled, No Show (unified with AC15's search-result count and the
  // backend's APPOINTMENT_COUNT_IGNORE_STATUSES — see searchPatientsForMergeHandler).
  const count = appointments.filter(
    (a) =>
      !["checkout", "incomplete", "abandoned", "canceled", "no_show"].includes(
        a.status,
      ),
  ).length;

  // Supabase's generated types treat these embeds as arrays even though
  // legal_guardian_id/family_contact_id are single-row FKs (to-one) — same
  // gotcha worked around elsewhere in this codebase.
  const guardian = Array.isArray(patient.guardian)
    ? patient.guardian[0]
    : patient.guardian;
  const family = Array.isArray(patient.family)
    ? patient.family[0]
    : patient.family;

  type CustomerRow = {
    id: string;
    first_name: string;
    last_name: string;
    email: string | null;
    mobile_phone_number: string | null;
  };

  let customerRow: CustomerRow | null = guardian ?? family ?? null;
  let relationship: PatientCustomerRelationship = guardian ? "ward" : "family";

  // Self-patient: no ward/family FK, so the only link back to a customer is
  // patients.email = customers.email (same convention as getSelfPatient.ts).
  if (!customerRow && patient.email) {
    const { data: selfCustomer } = await supabase
      .from("customers")
      .select("id, first_name, last_name, email, mobile_phone_number")
      .eq("email", patient.email)
      .limit(1)
      .maybeSingle();
    customerRow = selfCustomer ?? null;
    relationship = "self";
  }

  return {
    id: patient.id,
    mrn: patient.mrn,
    firstName: patient.first_name,
    lastName: patient.last_name,
    dateBirth: patient.date_birth,
    city: patient.city,
    state: patient.state,
    postalCode: patient.postal_code,
    country: patient.country,
    race: patient.race,
    ethnicity: patient.ethnicity,
    sex: patient.sex,
    linkedCustomer: customerRow
      ? {
          id: customerRow.id,
          name: `${customerRow.last_name}, ${customerRow.first_name}`,
          email: customerRow.email,
          phone: customerRow.mobile_phone_number,
          relationship,
        }
      : null,
    appointmentCount: count,
    appointments,
  };
};

export default getPatientForMerge;
